import { ButtonDropdown } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useId } from 'react';
import { type Contact } from './contacts';

type EstimateRecipientPickerProps = {
  recipient: Contact | null;
  contacts?: Contact[];
  /**
   * When omitted, the recipient is rendered as pinned text rather than a
   * dropdown — e.g. once an estimate is SENT and the contact is locked in.
   */
  onSelectRecipient?: (contact: Contact) => void;
};

const EstimateRecipientPicker: FC<EstimateRecipientPickerProps> = (props) => {
  const { recipient, contacts = [], onSelectRecipient } = props;
  const pinnedLabelId = useId();

  return (
    <>
      {onSelectRecipient ? (
        <>
          <label htmlFor="send-to-customer-contact">Send to</label>
          <ButtonDropdown
            id="send-to-customer-contact"
            variant="white"
            colour="black"
            size="sm"
            dropdownOptions={{
              className: 'w-72',
              items: contacts.map((contact) => ({
                label: <ContactMenuItemLabel contact={contact} />,
                onClick: () => onSelectRecipient(contact),
                selected: contact.publicId === recipient?.publicId,
              })),
            }}
          >
            {recipient?.name ?? 'No contact'}
            <UntitledIcon name="chevron-down" className="size-3" />
          </ButtonDropdown>
        </>
      ) : (
        <>
          <span id={pinnedLabelId}>Send to</span>
          <span aria-labelledby={pinnedLabelId} className="font-medium mr-2">
            {recipient?.name ?? 'No contact'}
          </span>
        </>
      )}
    </>
  );
};

type ContactMenuItemLabelProps = {
  contact: Contact;
};

const ContactMenuItemLabel: FC<ContactMenuItemLabelProps> = (props) => {
  const { contact } = props;
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="flex items-center gap-x-2">
        <span className="truncate font-medium">
          {contact.name ?? 'Unnamed contact'}
        </span>
        {contact.isPrimary && <span className="text-gray-500">Primary</span>}
      </span>
      {contact.email && (
        <span className="truncate text-gray-500">{contact.email}</span>
      )}
    </div>
  );
};

export default EstimateRecipientPicker;
